/** @file Validates and registers one context's binding table; dispatch happens in the listen() key handler. */
import { keyState } from './_internal/keyState';
import type { Binding } from './types';
import { validateTable } from './validateTable';

/** Throws when a binding's `context` differs from `context`, has no handler, or the table fails validateTable. */
function check(context: string, table: Binding[], handlers: Record<string, () => void>): void {
  const problems = validateTable(table, keyState.platform).map((e) => `${e.code}: ${e.message}`);
  for (const binding of table) {
    if (binding.context !== context) problems.push(`${binding.id} is declared for context "${binding.context}"`);
    if (!(binding.id in handlers)) problems.push(`${binding.id} has no handler`);
  }
  if (problems.length > 0) throw new Error(`invalid bindings for context "${context}": ${problems.join('; ')}`);
}

/**
 * Validates first (throws on RESERVED_KEY/CONFLICT/INVALID_CHORD, a context mismatch or a missing handler); handlers
 * are looked up by binding id, so register only a table you own handlers for (never `allBindings()`). Active while
 * `context` is `global` or pushed with pushContext. Idempotent disposer.
 */
export function registerBindings(context: string, table: Binding[], handlers: Record<string, () => void>): () => void {
  check(context, table, handlers);
  const registration = { context, table: [...table], handlers: { ...handlers } };
  keyState.registrations.push(registration);
  return () => {
    const i = keyState.registrations.indexOf(registration);
    if (i >= 0) keyState.registrations.splice(i, 1);
  };
}
